export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

export const GENERIC_ERROR = "Something went wrong. Please try again.";
export const ZOD_PARSE_ERROR = "Something went wrong on our end. Please try again later.";

function sanitizeErrorText(text: string, fallback: string): string {
  const trimmed = text.trim();
  if (!trimmed) return fallback;
  if (trimmed.startsWith("<") || trimmed.startsWith("<!")) return fallback;
  if (trimmed.length > 300) return fallback;
  return trimmed;
}

async function throwIfResponseNotOk(response: Response): Promise<void> {
  if (response.ok) return;
  const fallback = GENERIC_ERROR;
  let errorMessage = fallback;
  try {
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || fallback;
    } else {
      const errorText = await response.text();
      errorMessage = sanitizeErrorText(errorText, fallback);
    }
  } catch {
    // Ignore parse errors on error responses
  }
  throw new ApiError(errorMessage, response.status);
}

async function parseSuccessResponseBody<T = unknown>(
  response: Response,
): Promise<T> {
  const contentType = response.headers.get("content-type");
  try {
    if (contentType && contentType.includes("application/json")) {
      return (await response.json()) as T;
    }
    return (await response.text()) as T;
  } catch {
    return (await response.text()) as T;
  }
}

export async function getApi<T = unknown>(
  url: string,
  accessToken: string | undefined,
  responseType: "json" | "blob" = "json",
  signal?: AbortSignal,
): Promise<{ data: T }> {
  const response = await fetch(url, {
    method: "GET",
    signal,
    headers: {
      ...(responseType === "json" && { "Content-Type": "application/json" }),
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  await throwIfResponseNotOk(response);

  const data =
    responseType === "blob" ? await response.blob() : await response.json();

  return { data };
}
export async function postApi<T = unknown>(
  url: string,
  accessToken: string | undefined,
  body: Record<string, unknown>,
): Promise<{ data: T }> {
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(body),
  });

  await throwIfResponseNotOk(response);

  const data = await parseSuccessResponseBody<T>(response);

  return { data };
}

export async function putApi<T = unknown>(
  url: string,
  accessToken: string | undefined,
  body: Record<string, unknown>,
): Promise<{ data: T }> {
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(body),
  });

  await throwIfResponseNotOk(response);

  const data = await parseSuccessResponseBody<T>(response);

  return { data };
}

/** PUT endpoints that accept a plain text comment instead of a JSON object. */
export async function putRawText<T = unknown>(
  url: string,
  accessToken: string | undefined,
  body: string,
): Promise<{ data: T }> {
  const response = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "text/plain",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body,
  });
  await throwIfResponseNotOk(response);
  return { data: await parseSuccessResponseBody<T>(response) };
}

export async function deleteApi<T = unknown>(
  url: string,
  accessToken: string | undefined,
): Promise<{ data: T }> {
  const response = await fetch(url, {
    method: "DELETE",
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  await throwIfResponseNotOk(response);
  const data = await parseSuccessResponseBody<T>(response);
  return { data };
}

export async function postUrlEncoded<T = unknown>(
  url: string,
  accessToken: string | undefined,
  params: URLSearchParams,
  signal?: AbortSignal,
): Promise<{ data: T }> {
  const response = await fetch(url, {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: params.toString(),
  });

  await throwIfResponseNotOk(response);

  const data = await parseSuccessResponseBody<T>(response);

  return { data };
}

export async function postFormDataMultipart<T = unknown>(
  url: string,
  accessToken: string | undefined,
  formData: FormData,
  signal?: AbortSignal,
): Promise<{ data: T }> {
  const response = await fetch(url, {
    method: "POST",
    signal,
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: formData,
  });

  await throwIfResponseNotOk(response);

  const data = await parseSuccessResponseBody<T>(response);

  return { data };
}

export async function putFormDataMultipart<T = unknown>(
  url: string,
  accessToken: string | undefined,
  formData: FormData,
  signal?: AbortSignal,
): Promise<{ data: T }> {
  const response = await fetch(url, {
    method: "PUT",
    signal,
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: formData,
  });
  await throwIfResponseNotOk(response);
  const data = await parseSuccessResponseBody<T>(response);
  return { data };
}

export async function postEmptyBody<T = unknown>(
  url: string,
  accessToken: string | undefined,
  signal?: AbortSignal,
): Promise<{ data: T }> {
  const response = await fetch(url, {
    method: "POST",
    signal,
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  await throwIfResponseNotOk(response);

  const data = await parseSuccessResponseBody<T>(response);

  return { data };
}

/** POST with a raw JSON body (e.g. boolean `true` / `false`). */
export async function postRawJson<T = unknown>(
  url: string,
  accessToken: string | undefined,
  body: unknown,
  signal?: AbortSignal,
): Promise<{ data: T }> {
  const response = await fetch(url, {
    method: "POST",
    signal,
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify(body),
  });

  await throwIfResponseNotOk(response);

  const data = await parseSuccessResponseBody<T>(response);

  return { data };
}
